<script>
import TiptapEditor from '@/components/ux/bundleEditor/Tiptap-editor.vue';
import ComparisonImageSlider from '@/components/modals/Comparison-image-slider.vue';
import ArticleAdImages from '@/components/modals/Article-ad-images.vue';
import RemoveAnchorPopup from '@/components/modals/Remove-anchor-popup.vue';
import apiCall from '@/store/apiCall';
import URLS from '@/store/urls';
import bem from '@/utils/bem';

const b = bem('article-editor-tab');

export default {
  components: {
    TiptapEditor,
    ComparisonImageSlider,
    ArticleAdImages,
    RemoveAnchorPopup,
  },
  props: {
    body: { type: String, default: '' },
    initialBody: { type: String, default: '' },
    hasError: { type: Boolean, default: false },
  },
  emits: ['update:body'],
  data() {
    return {
      isShowComparisonImageSlider: false,
      comparisonImageSlider: {},
      isShowAdBlock: false,
      adBlockData: {},
      isShowRemoveAnchorPopup: false,
      selectedAnchorListNode: [],
    };
  },
  computed: {
    editorCustomButtons() {
      return [
        {
          text: 'Ads block',
          onAction: () => { this.isShowAdBlock = true; },
        },
        {
          text: 'Add Ad',
          onAction: () => {
            const content = this.addAdBlock();
            if (content) {
              this.$refs.tiptapEditor.insertContent(content);
            }
            this.adBlockData = {};
          },
        },
        {
          text: 'Upload Compare',
          onAction: () => { this.isShowComparisonImageSlider = true; },
        },
        {
          text: 'Add Compare',
          onAction: () => {
            const content = this.addCompareImage();
            if (content) {
              this.$refs.tiptapEditor.insertContent(content);
            }
            this.comparisonImageSlider = {};
          },
        },
        {
          text: 'Add title anchor',
          onAction: () => { this.addAnchorToHeading(); },
        },
        {
          text: 'Remove anchor',
          onAction: () => { this.removeAnchorFromHeadings(); },
        },
      ];
    },
  },
  methods: {
    b(el, mods) { return b(el, mods); },

    imageUploadHandler: async (file, success) => {
      const formData = new FormData();
      formData.append('folder', 'images-blog');
      formData.append('file', file.blob(), file.filename());
      const { data: { data: { url } } } = await apiCall({
        type: 'POST',
        headers: { 'content-type': 'multipart/form-data' },
        url: URLS.fileUpload,
        body: formData,
        isToken: true,
      });
      success(url);
      return url;
    },

    addCompareImage() {
      const { imageBeforeUri, imageAfterUri } = this.comparisonImageSlider;
      if (imageBeforeUri && imageAfterUri) {
        return { type: 'comparisonSlider', attrs: { imageBeforeUri, imageAfterUri } };
      }
      return null;
    },

    addAdBlock() {
      const {
        imageDesktopUri, imageAlt, title,
        description, buttonText, buttonLink,
      } = this.adBlockData;
      if (imageDesktopUri) {
        return {
          type: 'adBlock',
          attrs: {
            imageDesktopUri,
            imageAlt,
            title,
            description,
            buttonText,
            buttonLink,
          },
        };
      }
      return null;
    },

    addAnchorToHeading() {
      const editor = this.$refs.tiptapEditor?.getEditor();
      if (!editor) return;
      const { from } = editor.state.selection;
      const node = editor.state.doc.resolve(from).parent;
      if (node.type.name === 'heading') {
        const currentClass = node.attrs.class || '';
        if (!currentClass.includes('anchor')) {
          editor.chain().focus().updateAttributes('heading', {
            class: currentClass ? `${currentClass} anchor` : 'anchor',
          }).run();
        }
      }
    },

    removeAnchorFromHeadings() {
      const editor = this.$refs.tiptapEditor?.getEditor();
      if (!editor) return;
      const editorElement = editor.view.dom;
      const anchorListNode = [...editorElement.getElementsByClassName('anchor')];
      if (anchorListNode?.length) {
        this.selectedAnchorListNode = anchorListNode;
        this.isShowRemoveAnchorPopup = true;
      }
    },
  },
};
</script>

<template>
  <div :class="b('', { error: hasError })">
    <tiptap-editor
      ref="tiptapEditor"
      :height="2000"
      :width="1600"
      :image-upload-handler="imageUploadHandler"
      :custom-buttons="editorCustomButtons"
      :value="initialBody"
      @update:value="$emit('update:body', $event)"
    />

    <div v-if="isShowComparisonImageSlider">
      <ComparisonImageSlider
        v-model:show="isShowComparisonImageSlider"
        v-model:comparisonImageSlider="comparisonImageSlider"
      />
    </div>
    <div v-if="isShowAdBlock">
      <ArticleAdImages
        v-model:show="isShowAdBlock"
        v-model:adBlockData="adBlockData"
      />
    </div>
    <div v-if="isShowRemoveAnchorPopup">
      <RemoveAnchorPopup
        v-model:show="isShowRemoveAnchorPopup"
        v-model:selectedAnchorListNode="selectedAnchorListNode"
        :anchorListNode="selectedAnchorListNode"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.article-editor-tab {
  // Fill the remaining viewport height below header+tabs
  height: calc(100vh - 130px);
  overflow: hidden;

  // Override TipTap's inline width/height to fill container
  :deep(.tiptap-wrapper) {
    width: 100% !important;
    height: 100% !important;
    max-height: 100% !important;
  }

  &_error {
    outline: 2px solid var(--Error, #ff4d4f);
    outline-offset: -2px;
    border-radius: 4px;
  }
}
</style>
